import { Sentry } from '@nrx/error-tracking-web';

/**
 * Which consult surface a failure came from.
 *
 * One tag per fetch path plus `render` for the error boundary, because the four member-facing
 * messages are indistinguishable from each other and from a genuine 500. A JSON parse failure, a
 * CORS rejection, a `TypeError` inside a `.then` chain and a real backend error all render
 * "Unable to load sessions. Please try again later." — an operator triaging a member report needs
 * the tag to know which request even failed.
 */
export type ConsultErrorScope =
  | 'link-exchange'
  | 'webinars-fetch'
  | 'consultants-fetch'
  | 'webinar-registration'
  | 'render'
  /** `stripe.ts`: Stripe.js itself failed to load (usually an ad blocker or corporate proxy). */
  | 'stripe-load'
  /** `AdminFeeCardStep`: minting the Stripe SetupIntent failed. */
  | 'admin-fee-setup-intent'
  /** `AdminFeeCardStep`: the `PaymentElement` failed to MOUNT (Stripe.js loaded, but the element
   *  itself did not) — distinct from `admin-fee-setup-intent`, a different failure with a
   *  different likely cause, and the taxonomy exists so an operator can tell them apart. */
  | 'admin-fee-element-load'
  /** `AdminFeeCardStep`: `stripe.confirmSetup` returned a non-member-facing error (never a
   *  card decline or validation error — those are a business outcome, not a defect). */
  | 'admin-fee-stripe-confirm'
  /** `AdminFeeCardStep`: `confirmSetup` succeeded but the server-side confirm POST failed —
   *  the card IS stored at Stripe and only our record of it did not update. */
  | 'admin-fee-confirm'
  /** `AdminFeeCardStep`: resuming from a full-page 3-D Secure redirect — confirming the
   *  SetupIntent id Stripe handed back in the URL — failed. Distinct from `admin-fee-confirm`
   *  (the inline, no-redirect confirm path) so an operator can tell which path failed. */
  | 'admin-fee-confirm-resume';

/** A rejected fetch can be any thrown value, and Sentry only gets a stack from a real Error. */
function toError(err: unknown, scope: ConsultErrorScope): Error {
  if (err instanceof Error) return err;
  const wrapped = new Error(typeof err === 'string' ? err : `consult ${scope} failed`);
  wrapped.name = 'ConsultError';
  return wrapped;
}

/**
 * Report a consult failure to Sentry, tagged by surface.
 *
 * @remarks Deliberately takes NO member context (W6). This is a magic-link, PHI-adjacent surface
 * whose exchange response carries email, first name, last name, phone and state; `name` is on the
 * PHI denylist. `scrubPhiFromSentryEvent` runs as `beforeSend` and is a real backstop, but a
 * PHI-safe call site does not rely on it — so the signature simply cannot express member data, and
 * adding it would be a type error rather than something a reviewer has to catch.
 *
 * The member-facing string stays generic at every call site; this is the operator's half.
 *
 * No-op when `initSentryBrowser` found no DSN (local dev, un-provisioned builds): `captureException`
 * on an uninitialised client discards the event. Callers therefore need no environment check.
 */
export function reportConsultError(scope: ConsultErrorScope, err: unknown): void {
  Sentry.withScope((sentryScope) => {
    sentryScope.setTag('consult_scope', scope);
    Sentry.captureException(toError(err, scope));
  });
}
